import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { SystemContextService } from '@app/core/services/system-context.service';
import { SystemSettingsService } from '@app/core/services/system-settings.service';
import { AuthService } from '@app/core/auth/auth.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-menu',
    templateUrl: './app-menu.component.html',
    styleUrls: ['./app-menu.component.scss'],
    standalone: false
})
export class AppMenuComponent implements OnInit, OnDestroy {
    model: MenuItem[] = [];

    private userSub?: Subscription;

    constructor(
        private systemContext: SystemContextService,
        private router: Router,
        private settings: SystemSettingsService,
        private authService: AuthService
    ) {}

    get currentSystem(): string {
        const ctx = this.systemContext.getSystem();
        if (ctx) return ctx;
        // Fallback inteligente: deducir por URL si no hay contexto definido
        const url = this.router.url || '';
        if (url.startsWith('/administracion-blister')) return 'administracion-blister';
        if (url.startsWith('/rrhh')) return 'rrhh';
        if (url.startsWith('/medio-ambiente')) return 'medio-ambiente';
        if (url.startsWith('/hse')) return 'hse';
        return 'hse';
    }

    private hasLVClaims(user: any): boolean {
        if (Number(user?.superadmin) === 1) return true;
        const claims: string[] = user?.claims?.map((c: any) => c.nombre) ?? [];
        return claims.some((c) => c.startsWith('HSE_LISTAS_VERIFICACION_'));
    }

    private hasMaLvClaims(user: any): boolean {
        if (Number(user?.superadmin) === 1) return true;
        const claims: string[] = user?.claims?.map((c: any) => c.nombre) ?? [];
        return claims.some((c) => c.startsWith('MA_CHECKLISTS_'));
    }

    private hasClaim(user: any, claimName: string): boolean {
        if (Number(user?.superadmin) === 1) return true;
        return (user?.claims ?? []).some((claim: any) =>
            (typeof claim === 'string' ? claim : claim.nombre) === claimName
        );
    }

    private buildModel(user: any): void {
        const isHSE = this.currentSystem === 'hse';
        const isRRHH = this.currentSystem === 'rrhh';
        const isMA = this.currentSystem === 'medio-ambiente';
        const isAdminBlister = this.currentSystem === 'administracion-blister';

        // --- Home ---
        const homeSection: MenuItem = {
            label: 'Home',
            items: [
                {
                    label: 'Dashboard',
                    icon: 'pi pi-fw pi-home',
                    routerLink: [`/${this.currentSystem}`]
                }
            ]
        };

        // --- Cargas y Registros ---
        let middleSection: MenuItem | null = null;

        if (isHSE) {
            const cargasYRegistrosItems: MenuItem[] = [
                {
                    label: 'Gestión Vehicular',
                    icon: 'pi pi-fw pi-car',
                    routerLink: [`/${this.currentSystem}/gestionvehicular/home`]
                },
                {
                    label: 'Tarjeta de Observaciones',
                    icon: 'pi pi-fw pi-clipboard',
                    routerLink: [`/${this.currentSystem}/tarjetasobs/home`]
                },
                ...(this.hasLVClaims(user)
                    ? [{
                        label: 'Checklists',
                        icon: 'pi pi-fw pi-list-check',
                        routerLink: [`/${this.currentSystem}/listasverificacion/home`]
                    }]
                    : []),
            ];

            middleSection = { label: 'Cargas y Registros', items: cargasYRegistrosItems };
        } else if (isRRHH) {
            const rrhhItems: MenuItem[] = [
                {
                    label: 'Solicitudes',
                    icon: 'pi pi-fw pi-calendar-clock',
                    routerLink: [`/${this.currentSystem}/solicitudes/home`]
                }
            ];
            middleSection = { label: 'Cargas y Registros', items: rrhhItems };
        } else if (isMA) {
            const maItems: MenuItem[] = [
                ...(this.hasMaLvClaims(user)
                    ? [{
                        label: 'Checklists',
                        icon: 'pi pi-fw pi-list-check',
                        routerLink: [`/${this.currentSystem}/listasverificacion/home`]
                    }]
                    : [])
            ];
            middleSection = { label: 'Cargas y Registros', items: maItems };
        } else if (isAdminBlister) {
            const sys = 'administracion-blister' as const;
            const blisterItems: MenuItem[] = [];
            if (this.settings.isModuleEnabled(sys, 'gestion', true)) {
                blisterItems.push({ label: 'Gestión', icon: 'pi pi-fw pi-box', routerLink: [`/${this.currentSystem}/gestion`] });
            }
            if (this.settings.isModuleEnabled(sys, 'configuracion', true)) {
                blisterItems.push({ label: 'Configuración', icon: 'pi pi-fw pi-cog', routerLink: [`/${this.currentSystem}/configuracion`] });
            }
            middleSection = { label: 'Administración Blister', items: blisterItems };
        }

        // --- Administración (global, sin prefijo de sistema) ---
        const administracionItems: MenuItem[] = [
            ...(this.hasClaim(user, 'SISTEMA_USUARIOS') ? [{
                label: 'Usuarios',
                icon: 'pi pi-fw pi-user',
                routerLink: ['/usuarios/home']
            }] : []),
            ...(this.hasClaim(user, 'SISTEMA_PARAMETROS') ? [{
                label: 'Parámetros',
                icon: 'pi pi-fw pi-cog',
                routerLink: ['/parametros/home']
            }] : [])
        ];
        const administracionSection: MenuItem = {
            label: 'Administración',
            icon: 'pi pi-fw pi-briefcase',
            items: administracionItems
        };

        // --- Extras (FAQ, Calendario) ---
        const extrasSection: MenuItem = {
            label: 'Extras',
            items: [
                {
                    label: 'Calendario',
                    icon: 'pi pi-fw pi-calendar',
                    routerLink: ['/calendario/home']
                },
                {
                    label: 'Ayuda y accesos',
                    icon: 'pi pi-fw pi-question-circle',
                    routerLink: ['/faq']
                }
            ]
        };

        this.model = [
            homeSection,
            ...(middleSection ? [middleSection] : []),
            ...(administracionItems.length ? [administracionSection] : []),
            extrasSection
        ];
    }

    ngOnInit() {
        this.userSub = this.authService.currentUser$.subscribe((user) => {
            this.buildModel(user);
        });
    }

    ngOnDestroy() {
        this.userSub?.unsubscribe();
    }
}
